import { Component, Prop, Vue } from "vue-property-decorator";
import {
  IDynamicColumn,
  IDynamicSelectedColumn
} from "@/models/dynamicColumns";
import store from "@/store";

@Component({
  components: {
    "dynamic-column-cell": () =>
      import("@/components/app/global/dynamicColumns/dynamicColumnCell.vue")
  }
})
export class DynamicColumns extends Vue {
  @Prop({ required: true })
  public id!: string;

  public availableColumns: IDynamicColumn[] = [];
  public selectedColumns: IDynamicSelectedColumn[] = [];
  public defaultColumns: string[] = [];
  public defaultColumnWidth = "";

  public onColumnsResizedDebounced = this.$_.debounce(
    this.onColumnsResized,
    300
  );

  public created() {
    this.init();
  }

  public init() {
    let columns = this.loadFromLocalStorage();
    if (!columns.length) {
      columns = this.buildColumnsWithWidths(this.defaultColumnsFiltered);
    }
    this.selectedColumns = columns;
  }

  public buildColumnsWithWidths(initialColumns: string[]) {
    const columns: IDynamicSelectedColumn[] = [];
    initialColumns.forEach(column => {
      const columnObject: IDynamicSelectedColumn = {
        id: column
      };

      const selectedColumn = this.selectedColumns.find(sc => sc.id === column);
      if (selectedColumn && selectedColumn.width) {
        columnObject.width = selectedColumn.width;
      }

      columns.push(columnObject);
    });

    return columns;
  }

  public saveToLocalStorage() {
    store.dispatch("user/addMeta", {
      [this.localStorageKey]: JSON.stringify(this.selectedColumns)
    });
  }

  public loadFromLocalStorage(): any {
    const data = store?.getters["user/meta"](this.localStorageKey);
    if (!data) {
      return [];
    }

    return JSON.parse(data);
  }

  public get localStorageKey() {
    return `upm/ui/table/${this.id}`;
  }

  public get availableColumnsFiltered() {
    return this.$_.filter(
      this.availableColumns,
      column => !column.if || column.if(this)
    );
  }

  public get tableColumns() {
    const columns = this.$_.filter(
      this.$_.cloneDeep(this.availableColumnsFiltered),
      column => this.selectedColumns.some(c => column.id === c.id)
    );

    const sorted = this.$_.sortBy(columns, column =>
      this.selectedColumns.findIndex(c => column.id === c.id)
    );

    let defaultWidthObject = {};
    if (this.defaultColumnWidth) {
      defaultWidthObject = {
        style: { width: this.defaultColumnWidth }
      };
    }

    this.$_.forEach(sorted, column => {
      let selectedWidthObject = {};
      const selectedColumn = this.selectedColumns.find(
        sc => sc.id === column.id
      );
      if (selectedColumn && selectedColumn.width) {
        selectedWidthObject = {
          style: { width: selectedColumn.width }
        };
      }

      column.thAttr = this.$_.merge(
        {},
        defaultWidthObject,
        column.thAttr,
        selectedWidthObject
      );
    });

    return sorted;
  }

  public get defaultColumnsFiltered(): string[] {
    return this.$_.filter(this.$_.cloneDeep(this.defaultColumns), column =>
      this.$_.some(this.availableColumnsFiltered, ["id", column])
    );
  }

  public openManageColumnsModal() {
    // @ts-ignore
    this.$buefy.modal.open({
      component: () =>
        import("@/components/app/global/dynamicColumns/manageColumnsModal.vue"),
      parent: this,
      props: {
        defaultColumns: this.defaultColumnsFiltered,
        selectedColumns: this.selectedColumns.map(column => column.id),
        availableColumns: this.availableColumnsFiltered
      },
      width: 540,
      hasModalCard: true,
      events: {
        "columns-changed": (columns: string[]) => {
          this.$set(
            this,
            "selectedColumns",
            this.buildColumnsWithWidths(columns)
          );
          this.saveToLocalStorage();
        }
      }
    });
  }

  public onColumnsResized(data: Array<{ index: string; width: string }>) {
    this.selectedColumns.forEach((column, index) => {
      if (!data[index]) {
        return;
      }
      this.$set(column, "width", data[index].width);
    });
    this.saveToLocalStorage();
  }

  public resetWidths() {
    this.selectedColumns.forEach(column => this.$delete(column, "width"));
    this.saveToLocalStorage();
  }

  public get hasCustomWidths() {
    return this.selectedColumns.some(column => this.$_.has(column, "width"));
  }
}
